import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const CACHE_KEY = "atlas-user-group";

type UserGroup = {
  /** `null` mientras no se conoce (primer login sin cache). */
  isDevTester: boolean | null;
  /** `grupo_testing` de la empresa. `null` mientras no se conoce; tratar como 'A'. */
  grupo: string | null;
  /**
   * `company_profiles.plan`. `null` mientras no se conoce.
   *
   * Viaja acá y no en un hook propio porque esta consulta YA lee la fila de la empresa:
   * sumar la columna no cuesta un request más, y el cuestionario de onboarding necesita
   * el plan en dos pantallas (ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago)
   * donde una consulta extra sería la tercera en serie de un camino que además está
   * precargando imágenes.
   *
   * NO es el lugar del que sacar el plan para GATEAR features. Para eso está la semilla
   * `atlas-user-plan` + `getUserPlan` que usan `Modos`, `InterfazV2`, `AsistenteMode` y
   * compañía, con su convención de "desconocido no alcanza". Acá el plan se usa para DECIR
   * cosas (elegir un copy), no para repartir acceso.
   */
  plan: string | null;
};

type CachedUserGroup = { devTester: boolean; grupo: string; plan?: string | null };

/**
 * El grupo cacheado, sin fetch — para que el shell arranque con la interfaz que
 * corresponde en vez de con la de siempre hasta que llegue la cuenta
 * (ADR-2026-10-07-grupo-c-ve-la-interfaz-nueva). `null` si no hay cache.
 */
export function leerGrupoCacheado(): string | null {
  return readCache().grupo;
}

function readCache(): UserGroup {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { isDevTester: null, grupo: null, plan: null };
    const parsed = JSON.parse(raw) as CachedUserGroup;
    return {
      isDevTester: parsed.devTester === true,
      grupo: parsed.grupo ?? null,
      // Las entradas guardadas ANTES de que el plan entrara acá no lo traen: se leen como
      // "todavía no se sabe" y la consulta de abajo las corrige. Nada que migrar.
      plan: parsed.plan ?? null,
    };
  } catch {
    return { isDevTester: null, grupo: null, plan: null };
  }
}

/**
 * Lee `dev_tester`, `grupo_testing` y `plan` de `company_profiles` para el usuario
 * actual. Cachea en localStorage para evitar parpadeo de la UI (el nav cambia
 * según esto). Mientras no se conoce devuelve el último valor cacheado, o
 * todo en `null` en el primer login.
 *
 * Es la fuente única de fetch para el gating por grupo; `useDevTester` delega
 * en este hook. El `plan` se sumó para el cuestionario de onboarding, que tiene que
 * dejar de hablarle de la prueba gratis a quien acaba de pagar
 * (ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago): es la misma fila, sin
 * consulta extra. Ver la nota del campo: no es el plan con el que se gatean features.
 */
export function useUserGroup(): UserGroup {
  const [state, setState] = useState<UserGroup>(readCache);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!profile?.id_company) return;

        const { data: company } = await supabase
          .from("company_profiles")
          .select("dev_tester, grupo_testing, plan")
          .eq("id_company", profile.id_company)
          .single();
        if (cancelled || !company) return;

        const next: UserGroup = {
          isDevTester: company.dev_tester === true,
          grupo: (company.grupo_testing as string | null) ?? null,
          plan: (company.plan as string | null) ?? null,
        };
        setState(next);
        try {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              devTester: next.isDevTester,
              grupo: next.grupo,
              plan: next.plan,
            }),
          );
        } catch {
          /* noop */
        }
      } catch {
        /* noop — ante error mantenemos el valor cacheado */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
